import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations.jsx";

const LanguageContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function setPath(obj, path, value) {
  const keys = path.split(".");
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (cur[k] === undefined || cur[k] === null) {
      cur[k] = /^\d+$/.test(keys[i + 1]) ? [] : {};
    }
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
}

// Deep-merge CMS overrides (flat "about.p1.en" keys) onto the default translations.
function applyOverrides(base, overrides, lang) {
  const merged = JSON.parse(JSON.stringify(base));
  const suffix = `.${lang}`;
  Object.entries(overrides || {}).forEach(([key, value]) => {
    if (!key.endsWith(suffix) || typeof value !== "string" || !value.trim()) return;
    const path = key.slice(0, -suffix.length);
    const existing = path.split(".").reduce((o, k) => o?.[k], merged);
    if (Array.isArray(existing)) {
      setPath(merged, path, value.split(",").map((s) => s.trim()).filter(Boolean));
    } else {
      setPath(merged, path, value);
    }
  });
  return merged;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("tico-lang") : null;
    return saved === "pt" ? "pt" : "en";
  });
  const [overrides, setOverrides] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem("tico-lang", lang);
      document.documentElement.lang = lang === "pt" ? "pt-BR" : "en";
    } catch (e) {
      // ignore
    }
  }, [lang]);

  useEffect(() => {
    fetch(`${API}/content`)
      .then((r) => r.json())
      .then((d) => setOverrides(d?.overrides || {}))
      .catch(() => setOverrides({}));
  }, []);

  const t = useMemo(
    () => applyOverrides(translations[lang], overrides || {}, lang),
    [lang, overrides]
  );

  const value = {
    lang,
    setLang,
    toggleLang: () => setLang((l) => (l === "en" ? "pt" : "en")),
    t,
  };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
